/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiUser, FiLock, FiLogIn, FiPhone, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ReactInputMask from 'react-input-mask';

import Modal from '~/components/Modal';
import { useToast } from '~/hooks/toast';
import loadingGif from '~/assets/img/loadingMusic.gif';

import Button from '~/components/Button';
import TermsUse from '~/components/TermsUse';
import { formatTextCapitalize } from '~/utils/formatTextCapitalize';
import { useCreateUserMutation } from '~/graphql/generated/graphql';

import {
  BackgroundSectionRegister,
  DivSectionLeftRegister,
  DivSectionRightRegister,
} from './styles';

import {
  Container as InputContainer,
  Error as InputError,
} from '~/components/Input/styles';

import {
  Container as InputMContainer,
  Error as InputMError,
} from '~/components/InputM/styles';

import {
  Container as CheckboxContainer,
  Error as CheckboxError,
} from '~/components/Checkbox/styles';

const schema = Yup.object().shape({
  nickname: Yup.string()
    .trim()
    .required('Nome obrigatório')
    .max(45, 'No máximo 45 caracteres'),
  email: Yup.string()
    .trim()
    .required('E-mail obrigatório')
    .email('Insira um E-mail válido')
    .max(254, 'No máximo 254 caracteres'),
  contact: Yup.string()
    .required('Número obrigatório')
    .max(20, 'No máximo 20 caracteres')
    .test('min-digits', 'No mínimo 10 números', value => {
      if (!value) return false;
      const digits = value.replace(/[^0-9]/g, '');
      return digits.length >= 10;
    }),
  password: Yup.string()
    .required('Insira uma senha')
    .min(6, 'No mínimo 6 dígitos'),
  confirmPassword: Yup.string()
    .required('Insira uma senha')
    .oneOf([Yup.ref('password'), null], 'As senhas devem corresponder'),
  terms: Yup.boolean().oneOf([true], 'Você deve aceitar os termos e condições'),
});

export default function SignUp() {
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [modalShow, setModalShow] = useState('');
  const [focusedField, setFocusedField] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nickname: '',
      email: '',
      contact: '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
  });

  const [createUser, { loading }] = useCreateUserMutation();

  useEffect(() => {
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'Cadastro de Usuário',
        status: 'Sucesso',
      });
    }
  }, []);

  const onSubmit = useCallback(
    async data => {
      try {
        const validContact = data.contact.replace(/[^0-9]/g, '');

        const response = await createUser({
          variables: {
            data: {
              nickname: formatTextCapitalize(data.nickname.trim()),
              email: data.email.trim().toLowerCase(),
              contact: `+55${validContact}`,
              password: data.password,
              confirmPassword: data.confirmPassword,
              terms: data.terms,
            },
          },
        });

        navigate(`/validation/${response.data.createUser}`);

        addToast({
          type: 'success',
          title: 'Cadastro realizado!',
          description: 'Verifique seu E-mail!',
        });
      } catch (error) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          error.graphQLErrors.forEach(graphqlError => {
            addToast({
              type: 'error',
              title: 'Erro no cadastro',
              description: graphqlError.message,
            });
          });
          return;
        }

        if (error.networkError) {
          addToast({
            type: 'error',
            title: 'Erro na autenticação',
            description:
              'Serviço de dados indisponível no momento, favor tente mais tarde!',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description: 'Tente novamente',
        });
      }
    },
    [addToast, createUser, navigate]
  );

  return (
    <BackgroundSectionRegister>
      <DivSectionLeftRegister>
        <span>Crie uma conta grátis</span>
        <p>
          ou <Link to="/login">acesse sua conta</Link>
        </p>
      </DivSectionLeftRegister>
      <DivSectionRightRegister>
        <p>Cadastre-se abaixo</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputContainer
            isFocused={focusedField === 'nickname'}
            isFilled={false}
            isErrored={!!errors.nickname}
            textTranform="capitalize"
          >
            <FiUser size={20} className="lock" />
            <input
              {...register('nickname')}
              type="text"
              placeholder="Nome artístico"
              maxLength={45}
              onFocus={() => setFocusedField('nickname')}
              onBlur={() => setFocusedField('')}
              autoComplete="off"
            />
            {errors.nickname && (
              <InputError title={errors.nickname.message}>
                <FiAlertCircle color="#c53030" size={20} />
              </InputError>
            )}
          </InputContainer>

          <InputContainer
            isFocused={focusedField === 'email'}
            isFilled={false}
            isErrored={!!errors.email}
            textTranform="lowercase"
          >
            <FiMail size={20} className="lock" />
            <input
              {...register('email')}
              type="email"
              placeholder="Email"
              maxLength={254}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField('')}
              autoComplete="off"
            />
            {errors.email && (
              <InputError title={errors.email.message}>
                <FiAlertCircle color="#c53030" size={20} />
              </InputError>
            )}
          </InputContainer>

          <Controller
            name="contact"
            control={control}
            render={({ field }) => (
              <InputMContainer
                isFocused={focusedField === 'contact'}
                isFilled={false}
                isErrored={!!errors.contact}
              >
                <FiPhone size={20} />
                <ReactInputMask
                  {...field}
                  mask="(99) 99999-9999"
                  type="tel"
                  placeholder="Celular"
                  maxLength={20}
                  onFocus={() => setFocusedField('contact')}
                  onBlur={() => setFocusedField('')}
                  autoComplete="off"
                />
                {errors.contact && (
                  <InputMError title={errors.contact.message}>
                    <FiAlertCircle color="#c53030" size={20} />
                  </InputMError>
                )}
              </InputMContainer>
            )}
          />

          <InputContainer
            isFocused={focusedField === 'password'}
            isFilled={false}
            isErrored={!!errors.password}
          >
            <FiLock size={20} className="lock" />
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              placeholder="Senha"
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField('')}
              autoComplete="off"
            />
            {showPassword
              ? <FiEyeOff size={20} className="view" onClick={() => setShowPassword(false)} />
              : <FiEye size={20} className="view" onClick={() => setShowPassword(true)} />
            }
            {errors.password && (
              <InputError title={errors.password.message}>
                <FiAlertCircle color="#c53030" size={20} />
              </InputError>
            )}
          </InputContainer>

          <InputContainer
            isFocused={focusedField === 'confirmPassword'}
            isFilled={false}
            isErrored={!!errors.confirmPassword}
          >
            <FiLock size={20} className="lock" />
            <input
              {...register('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar senha"
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField('')}
              autoComplete="off"
            />
            {showConfirmPassword
              ? <FiEyeOff size={20} className="view" onClick={() => setShowConfirmPassword(false)} />
              : <FiEye size={20} className="view" onClick={() => setShowConfirmPassword(true)} />
            }
            {errors.confirmPassword && (
              <InputError title={errors.confirmPassword.message}>
                <FiAlertCircle color="#c53030" size={20} />
              </InputError>
            )}
          </InputContainer>

          <div className="termos">
            <CheckboxContainer isErrored={!!errors.terms}>
              <div>
                <input
                  type="checkbox"
                  id="terms"
                  {...register('terms')}
                />
                <span>
                  Aceito os{' '}
                  <button type="button" onClick={() => setModalShow('terms')}>
                    termos e condições
                  </button>
                  {modalShow === 'terms' && (
                    <Modal
                      isOpen={modalShow}
                      onClickClose={() => setModalShow('')}
                    >
                      <TermsUse />
                    </Modal>
                  )}
                </span>
              </div>
              {errors.terms && (
                <CheckboxError title={errors.terms.message}>
                  <FiAlertCircle color="#c53030" size={20} />
                </CheckboxError>
              )}
            </CheckboxContainer>
          </div>

          <Button type="submit" disabled={loading} className={loading ? 'active' : ''}>
            Continuar
          </Button>
          {loading && <img src={loadingGif} alt="Carregando..." width="50px" />}
        </form>
        <Link to="/login" className="returnLogin">
          <FiLogIn /> Fazer login
        </Link>
      </DivSectionRightRegister>
    </BackgroundSectionRegister>
  );
}
